import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/axios.ts';
import { toast } from 'react-hot-toast';
import getApiErrorMessage from '../utils/helper';

export interface Contribution {
  id: number;
  member:{ 
    id: number;
    name: string;
    phone_number: string;
    email: string;
  };
  drive: string;
  amount: number;
  contribution_date: string;
  status: 'Complete' | 'Partial' | 'Pending';
  method: string;
  reference: string;
  welfare_plan_id?: number | null;
  welfare_plan_name?: string | null;
}

export interface ContributionFilters {
  search?: string;
  drive?: string;
  status?: string;
  method?: string;
  sort_field?: string;
  sort_direction?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  type?: 'excel' | 'csv' | 'pdf';
}

export interface ContributionStats {
  total: number;
  complete: number;
  partial: number;
  pending: number;
  total_amount: number;
  pending_amount: number;
}

// Hook to fetch contributions with filters
export const useContributions = (filters: ContributionFilters = {}) => {
  return useQuery({
    queryKey: ['contributions', filters],
    queryFn: async () => {
      const { data } = await api.get('/contributions', { params: filters });
      return data;
    },
  });
};

// Hook to fetch contribution statistics
export const useContributionStats = (filters: ContributionFilters = {}) => {
  return useQuery({
    queryKey: ['contribution-stats', filters],
    queryFn: async () => {
      const { data } = await api.get('/contributions/statistics', { params: filters });
      return data;
    },
  });
};

// Hook to create a new contribution
export const useCreateContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contribution: Omit<Contribution, 'id'>) => {
      const { data } = await api.post('/contributions', contribution);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
      toast.success('Contribution recorded successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};
// Hook to get contributions template
export const useGetContributionTemplate = () => {
  return useMutation({
    mutationFn: async () => {
      const response = await api.get(
        '/contributions/downloadContributionsTemplate',
        { responseType: 'blob' } // IMPORTANT for files
      );

      // Trigger file download in browser
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'MembersContributionTemplate.xlsx');
      document.body.appendChild(link);
      link.click();

      return true;
    },
    onSuccess: () => {
      toast.success('Template downloaded');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to upload template
export const useBulkUploadContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await api.post(
        '/contributions/upload',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] }); // Invalidate kit balance
      
      toast.success('Contribution uploaded successfully');
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to update a contribution
export const useUpdateContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...contribution }: Contribution) => {
      const { data } = await api.put(`/contributions/${id}`, contribution);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] }); // Invalidate kit balance
      
      // Show detailed success message with balance change info
      if (data?.data?.amount_change !== undefined) {
        const change = data.data.amount_change;
        if (change > 0) {
          toast.success(`Contribution updated successfully. KSh ${change.toLocaleString()} added to group balance.`);
        } else if (change < 0) {
          toast.success(`Contribution updated successfully. KSh ${Math.abs(change).toLocaleString()} deducted from group balance.`);
        } else {
          toast.success('Contribution updated successfully. No balance change.');
        }
      } else {
        toast.success('Contribution updated successfully');
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to update contribution');
    },
  });
};

// Hook to delete a contribution
export const useDeleteContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.delete(`/contributions/${id}`);
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
      queryClient.invalidateQueries({ queryKey: ['kit-balance'] }); // Invalidate kit balance
      
      // Show detailed success message with balance deduction info
      if (data?.data?.deleted_amount) {
        toast.success(`Contribution deleted successfully. KSh ${data.data.deleted_amount.toLocaleString()} deducted from group balance.`);
      } else {
        toast.success('Contribution deleted successfully');
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to delete contribution');
    },
  });
};

// Hook to export contributions
export const useExportContributions = () => {
  return useMutation({
    mutationFn: async (filters: ContributionFilters = {}) => {
      const response = await api.get('/contributions/export-data', {
        params: filters,
        responseType: 'blob',
      });
      
      // Create a download link with whatever the backend returns
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Use the filename from the backend response headers if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'contributions';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // If no filename from headers, determine from type parameter
      if (filename === 'contributions') {
        const type = filters.type || 'excel';
        const extensions = {
          'excel': 'xlsx',
          'csv': 'csv', 
          'pdf': 'pdf'
        };
        filename = `contributions.${extensions[type] || 'xlsx'}`;
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });
};

// Hook to fetch a single contribution
export const useContribution = (id: number) => {
  return useQuery({
    queryKey: ['contribution', id],
    queryFn: async () => {
      const { data } = await api.get(`/contributions/${id}`);
      return data as Contribution;
    },
    enabled: !!id,
  });
};

// Hook to pay contribution via M-Pesa
export const usePayContribution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      amount: number;
      notes?: string;
      phone_number?: string;
      member_id?: number;
      drive_id?: number;
      drive_contributions?: Array<{
        drive_id: number;
        amount: number;
      }>;
    }) => {
      const { data: response } = await api.post('/contributions/pay', data);
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['contributions'] });
      queryClient.invalidateQueries({ queryKey: ['contribution-stats'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      const msg = data?.message || 'Contribution payment initiated. Please check your phone for M-Pesa prompt.';
      toast.success(msg);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error) || 'Failed to initiate contribution payment');
    },
  });
};

// Hook to fetch member-specific contributions (for dashboard)
export const useMemberContributions = (filters: ContributionFilters = {}) => {
  return useQuery({
    queryKey: ['member-contributions', filters],
    queryFn: async () => {
      const { data } = await api.get('/contributions', { params: filters });
      return data;
    },
  });
};

// Hook to fetch member-specific contribution statistics
export const useMemberContributionStats = (filters: ContributionFilters = {}) => {
  return useQuery({
    queryKey: ['member-contribution-stats', filters],
    queryFn: async () => {
      const { data } = await api.get('/contributions/statistics', { params: filters });
      return data;
    },
  });
};
